import React from 'react';
import { Box, Paper, Typography, alpha } from '@mui/material';
import {
  CreditCard as CardIcon,
  TrendingDown as UsedIcon,
  RequestQuoteOutlined as DueIcon,
  Stars as RewardsIcon,
} from '@mui/icons-material';
import { formatCurrency } from '../../utils/formatters';

const Tile = ({ icon, label, value, hint, color }) => (
  <Paper sx={{ p: { xs: 1.75, sm: 2.5 }, display: 'flex', gap: 1.75, alignItems: 'center', minWidth: 0 }}>
    <Box
      sx={{
        width: 40,
        height: 40,
        borderRadius: 2,
        flexShrink: 0,
        display: { xs: 'none', sm: 'flex' },
        alignItems: 'center',
        justifyContent: 'center',
        color: `${color}.main`,
        bgcolor: (t) => alpha(t.palette[color].main, 0.1),
      }}
    >
      {icon}
    </Box>
    <Box sx={{ minWidth: 0 }}>
      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }} noWrap component="div">
        {label}
      </Typography>
      <Typography sx={{ fontWeight: 700, fontSize: { xs: '1rem', sm: '1.25rem' }, fontVariantNumeric: 'tabular-nums' }} noWrap>
        {value}
      </Typography>
      {hint && (
        <Typography variant="caption" color="text.secondary" noWrap component="div">
          {hint}
        </Typography>
      )}
    </Box>
  </Paper>
);

// Totals across the cards currently shown (respects search / filter / archive status)
const CreditCardsSummary = ({ cards }) => {
  // Linked cards share their primary card's limit, so count each limit once
  const limitHolders = cards.filter((card) => card.isPrimary || !card.primaryCardId);
  const totalLimit = limitHolders.reduce((sum, card) => sum + (card.totalLimit || 0), 0);
  const totalUsed = cards.reduce((sum, card) => sum + (card.usedAmount || 0), 0);
  const billsDue = cards.filter((card) => ['unpaid', 'partially_paid', 'overdue'].includes(card.paymentStatus));
  const totalDue = billsDue.reduce((sum, card) => sum + Math.max(0, (card.billAmount || 0) - (card.paymentsMade || 0)), 0);
  const totalPoints = cards.reduce((sum, card) => sum + (card.rewards_points || 0), 0);
  const usedPercent = totalLimit > 0 ? Math.round((totalUsed / totalLimit) * 100) : 0;

  return (
    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(4, 1fr)' }, gap: { xs: 1.5, sm: 2 }, mb: 3 }}>
      <Tile icon={<CardIcon />} color="primary" label="Total Limit" value={formatCurrency(totalLimit)} hint={`${cards.length} card${cards.length === 1 ? '' : 's'}`} />
      <Tile icon={<UsedIcon />} color="error" label="Used" value={formatCurrency(totalUsed)} hint={`${usedPercent}% of limit`} />
      <Tile
        icon={<DueIcon />}
        color="warning"
        label="Bills Due"
        value={formatCurrency(totalDue)}
        hint={billsDue.length ? `${billsDue.length} bill${billsDue.length === 1 ? '' : 's'} to pay` : 'All clear'}
      />
      <Tile icon={<RewardsIcon />} color="secondary" label="Reward Points" value={totalPoints.toLocaleString('en-IN')} />
    </Box>
  );
};

export default CreditCardsSummary;
